/**
 * TagInput Component
 *
 * Tag-style input for entering multiple values (e.g., banned words).
 * Type text and press Enter to add as a tag pill. Click × to remove.
 *
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';

/**
 * Render a tag input field.
 *
 * @param {string} key - State key (stores comma-separated string)
 * @param {string} label - Field label
 * @param {string} help - Tooltip help text
 * @param {Object} state - State object containing value
 * @param {Function} onUpdate - Callback when value changes (key, value)
 */
export function TagInput(key, label, help, state, onUpdate) {
    // Parse existing value into array
    const getTags = () => {
        const val = state[key] || '';
        return val.split(',').map(t => t.trim()).filter(t => t.length > 0);
    };

    // Convert array back to comma-separated string
    const setTags = (tags) => {
        state[key] = tags.join(',');
        if (onUpdate) onUpdate(key, state[key]);
        renderTags();
    };

    // Container for tag pills
    const tagsContainer = el('div', { class: 'presszone-comments-tag-input__tags' });

    // Render all tag pills
    const renderTags = () => {
        tagsContainer.innerHTML = '';
        const tags = getTags();
        tags.forEach((tag, index) => {
            const pill = el('span', { class: 'presszone-comments-tag-input__tag' },
                el('span', { class: 'presszone-comments-tag-input__tag-text' }, tag),
                el('button', {
                    type: 'button',
                    class: 'presszone-comments-tag-input__tag-remove',
                    onclick: (e) => {
                        e.preventDefault();
                        const newTags = getTags().filter((_, i) => i !== index);
                        setTags(newTags);
                    },
                    'aria-label': __('Remove', 'comments-press-zone')
                }, '×')
            );
            tagsContainer.appendChild(pill);
        });
    };

    // Input field
    const input = el('input', {
        type: 'text',
        class: 'presszone-comments-tag-input__input',
        placeholder: __('Type and press Enter to add...', 'comments-press-zone'),
        onkeydown: (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const value = input.value.trim();
                if (value) {
                    const tags = getTags();
                    if (!tags.includes(value)) {
                        tags.push(value);
                        setTags(tags);
                    }
                    input.value = '';
                }
            }
        },
        onblur: (e) => {
            const value = input.value.trim();
            if (value) {
                const tags = getTags();
                if (!tags.includes(value)) {
                    tags.push(value);
                    setTags(tags);
                }
                input.value = '';
            }
        }
    });

    // Initial render
    renderTags();

    return el('div', { class: 'presszone-comments-form-group' },
        el('label', { class: 'presszone-comments-label--settings' },
            label,
            help ? el('span', { class: 'presszone-comments-tooltip', title: help }, 'i') : null
        ),
        el('div', { class: 'presszone-comments-tag-input' },
            tagsContainer,
            input
        )
    );
}

export default TagInput;
